import { roundNutritionDisplay } from '@/utils/nutrition-display-format';

const GRAM_UNIT_PATTERN = /^(g|gram|grams)$/i;
const ML_UNIT_ALIASES = ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres'];
const VOLUME_TO_ML: Record<string, number> = {
  ml: 1,
  milliliter: 1,
  milliliters: 1,
  millilitre: 1,
  millilitres: 1,
  l: 1000,
  liter: 1000,
  liters: 1000,
  litre: 1000,
  litres: 1000,
  tsp: 4.92892,
  teaspoon: 4.92892,
  teaspoons: 4.92892,
  tbsp: 14.7868,
  tablespoon: 14.7868,
  tablespoons: 14.7868,
  cup: 236.588,
  cups: 236.588,
  floz: 29.5735,
  'fl oz': 29.5735,
  fluidounce: 29.5735,
  fluidounces: 29.5735,
  pint: 473.176,
  pints: 473.176,
  quart: 946.353,
  quarts: 946.353,
  gallon: 3785.41,
  gallons: 3785.41,
};

export const roundTo2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

/** Same display rules as custom recipe (`roundNutritionDisplay`): up to 2 decimals, trims trailing zeros. */
export const formatMacro = (val: number | undefined | null): string => {
  if (typeof val !== 'number' || !Number.isFinite(val)) return '0';
  return roundNutritionDisplay(val);
};

export const toPositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

export const isGramUnit = (value: unknown): boolean => GRAM_UNIT_PATTERN.test(String(value ?? '').trim());

const normalizeUnitKey = (value: unknown): string => String(value ?? '').trim().toLowerCase();
const normalizeVolumeUnitKey = (value: unknown): string => normalizeUnitKey(value).replace(/\./g, '').replace(/\s+/g, ' ');

export const resolveGramsPerMl = (
  unitWeightsByKey?: Record<string, unknown> | null,
  explicitGramsPerMl?: unknown,
): number | null => {
  const explicit = toPositiveNumber(explicitGramsPerMl);
  if (explicit != null) return roundTo2(explicit);
  if (!unitWeightsByKey || typeof unitWeightsByKey !== 'object') return null;

  for (const alias of ML_UNIT_ALIASES) {
    const grams = toPositiveNumber((unitWeightsByKey as Record<string, unknown>)[normalizeUnitKey(alias)]);
    if (grams != null) return roundTo2(grams);
  }

  return null;
};

export const isVolumeUnit = (unit: unknown): boolean => {
  const normalized = normalizeVolumeUnitKey(unit);
  if (!normalized) return false;
  if (ML_UNIT_ALIASES.includes(normalized)) return true;
  return VOLUME_TO_ML[normalized] != null;
};

export const shouldShowDistinctMlValue = (grams: unknown, ml: unknown): boolean => {
  const gramsNum = toPositiveNumber(grams);
  const mlNum = toPositiveNumber(ml);
  if (gramsNum == null || mlNum == null) return false;
  return Math.abs(roundTo2(gramsNum) - roundTo2(mlNum)) >= 0.01;
};

export const unitAmountToMl = (amount: unknown, unit: unknown): number | null => {
  const amountNum = toPositiveNumber(amount);
  if (amountNum == null) return null;
  const normalized = normalizeVolumeUnitKey(unit);
  if (!normalized) return null;
  const factor = VOLUME_TO_ML[normalized];
  if (factor == null || !Number.isFinite(factor) || factor <= 0) return null;
  return roundTo2(amountNum * factor);
};

export const gramsToMl = (grams: unknown, gramsPerMl: unknown): number | null => {
  const gramsNum = toPositiveNumber(grams);
  const gramsPerMlNum = toPositiveNumber(gramsPerMl);
  if (gramsNum == null || gramsPerMlNum == null) return null;
  return roundTo2(gramsNum / gramsPerMlNum);
};

export const mlToGrams = (ml: unknown, gramsPerMl: unknown): number | null => {
  const mlNum = toPositiveNumber(ml);
  const gramsPerMlNum = toPositiveNumber(gramsPerMl);
  if (mlNum == null || gramsPerMlNum == null) return null;
  return roundTo2(mlNum * gramsPerMlNum);
};

export const formatGramMlWeightLabel = (
  unit: string,
  gramsPerUnit: unknown,
  gramsPerMl: unknown,
): string => {
  const grams = toPositiveNumber(gramsPerUnit);
  if (grams == null) return unit;

  const gramsText = `${roundTo2(grams)}g`;
  const ml = gramsToMl(grams, gramsPerMl);
  if (ml == null) {
    return `${unit} (${gramsText})`;
  }
  if (!shouldShowDistinctMlValue(grams, ml)) {
    return `${unit} (${gramsText})`;
  }

  return `${unit} (${gramsText} / ${roundTo2(ml)}ml)`;
};
